import * as THREE from "three";
import { LOOKS, buildEnvironment } from "../look";
import { AnimRig } from "../rig";
import { buildTowerModel } from "../views/towerModel";
import { lib } from "../views/common";
import type { HqCopy } from "../assets";
import { SkinKits } from "./kit/runtime";
import { WornSkins } from "./wornSkins";
import { petModelName } from "./kits/pets";
import { towerModelName } from "./kit/towerModels";
import { glContexts } from "../../infra/glContexts";
import { HqMaterials } from "../hqMaterials";
import { FortressCompanion } from "../views/fortressCompanion";
import { SkinFx } from "./kit/fx";

/** What the picker previews: a tower type at a tier, the fortress at a tier, or a troop. */
export type PreviewTarget =
  | { readonly kind: "tower"; readonly archId: string; readonly tier: number }
  | { readonly kind: "fortress"; readonly tier: number }
  | { readonly kind: "troop" }
  /** docs/PETS.md: a pet in a pet skin (its own model when it ships, else the stock pet tinted). */
  | { readonly kind: "pet"; readonly petId: string };

/**
 * The skin picker's turntable (FINISH_PLAN WP8 "previews"): the real model, in the real skin, slowly turning, lit by
 * the game's own look — so what the player picks is what the map will show. A small WebGL canvas of its own (like
 * iconRender.ts), alive only while the picker is open: `dispose` frees its context. A drag spins it (the picker reads
 * the finger and calls `grab` / `spinBy` / `release`: the render layer reads no pointer events, ADR 0004); it runs its
 * idle clip. A model the hub has not loaded (a fortress skin's own GLB, the troop) is fetched first.
 */
export class SkinPreview {
  readonly canvas: HTMLCanvasElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(SkinPreview.FovDeg, 1, 0.05, 100);
  private model: THREE.Object3D | null = null;
  /** The private model copies on show (AssetLibrary.copies), freed with it. */
  private copies: ReadonlyMap<string, HqCopy> | null = null;
  /**
   * Seconds until the next showcase one-shot (Infinity: none), the clips it cycles through and which is next: a troop's
   * two blows, a pet coat's flourish (skins v2: its own clip, art/unitkit/pet_flourish.py).
   */
  private showcaseIn = Infinity;
  private showcase: readonly string[] = [];
  private showcaseNext = 0;
  private showcaseEvery = Infinity;
  /** A previewed troop swings its weapon this often. */
  static readonly TroopShowcaseSeconds = 3.2;
  /** A previewed pet coat plays its flourish this often (its first a beat after it appears). */
  static readonly PetShowcaseSeconds = 4.5;
  static readonly PetShowcaseFirst = 1.2;
  private rig: AnimRig | null = null;
  /** A fortress skin's flyers (the Dragonlord's dragon round the keep). */
  private companion: FortressCompanion | null = null;
  /** The shared material clock as this preview last left it: when nothing else moved it (the hub paused), we do. */
  private clock = -1;
  private raf = 0;
  private last = 0;
  private spin = 0;
  /** The kind last shown: a new kind starts the turn at its own angle (`StartSpin`). */
  private shownKind: PreviewTarget["kind"] | null = null;
  /**
   * The turn a kind starts at. A tower opens on the side the level camera sees (the model's +x +z diagonal, the ring
   * sockets' index 3: kit/sockets.ts), as the thumbnails and the kit sheet show it; the others open on their front.
   */
  static readonly StartSpin: Readonly<Record<PreviewTarget["kind"], number>> = { tower: (3 * Math.PI) / 4, fortress: 0, troop: 0, pet: 0 };
  private velocity = SkinPreview.IdleSpin;
  private held = false;
  private token = 0;
  private disposed = false;
  private attached = false;
  /** A narrow lens, near-orthographic like the game camera, looking down 30° as the map does. */
  static readonly FovDeg = 24;
  static readonly TiltDeg = 30;
  /** Radians a second when left alone; a flick decays back to it. */
  static readonly IdleSpin = 0.45;
  static readonly SpinDecay = 2.5;
  /** Radians per CSS px of drag. */
  static readonly DragGain = 0.012;
  /** Floor on a drag step's duration, so two moves in the same millisecond cannot make an infinite flick. */
  static readonly MinDragSeconds = 1e-3;
  /** The model's bounding sphere fills this fraction of the frame's height. */
  static readonly Fill = 0.86;
  /** Half the larger extent, a little over for the turn's corners. */
  static readonly ExtentToRadius = 0.58;
  /** Device-pixel cap: the canvas is small, 2× is sharp on a phone. */
  static readonly MaxDpr = 2;

  /** A shadow catcher under the model (the Shop's preview stands it on the game's grass picture). */
  private ground: THREE.Mesh | null = null;
  private key: THREE.DirectionalLight | null = null;

  constructor(width: number, height: number, grounded = false) {
    this.canvas = document.createElement("canvas");
    this.canvas.className = "skinprev";
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true, powerPreference: "low-power" });
    this.closeContext = glContexts.open("skins");
    this.renderer.setPixelRatio(Math.min(SkinPreview.MaxDpr, window.devicePixelRatio || 1));
    this.renderer.setSize(width, height, true);
    this.renderer.setClearAlpha(0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    const look = LOOKS.painterly;
    this.renderer.toneMappingExposure = look.exposure;
    this.scene.environment = buildEnvironment(this.renderer, look);
    const key = new THREE.DirectionalLight(look.sun.color, look.sun.intensity);
    key.position.set(-3.2, 5.0, 3.6);
    if (grounded) {
      // the model casts its own shadow onto the picture of the game's lawn behind the canvas
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      key.castShadow = true;
      key.shadow.mapSize.set(1024, 1024);
      this.key = key;
      this.ground = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShadowMaterial({ opacity: 0.35 }));
      this.ground.rotation.x = -Math.PI / 2;
      this.ground.receiveShadow = true;
      this.scene.add(this.ground, key.target);
    }
    const fill = new THREE.DirectionalLight(look.fill.color, look.fill.intensity);
    fill.position.set(3.4, 1.6, -3.0);
    this.scene.add(key, fill, new THREE.HemisphereLight(look.hemi.sky, look.hemi.ground, look.hemi.intensity));
    this.camera.aspect = width / height;
  }

  /** Whether a pet's model (in `skin`, or its stock one) ships, so the turntable can show it at all. */
  static canShowPet(petId: string, skin: string | null): boolean {
    // a pet whose own model has not shipped is shown by its picture, never as the stand-in pet (Pets v3)
    const L = lib();
    return !!L && L.ships(petModelName(petId)) && L.ships(WornSkins.petModel(petId, skin));
  }

  /** A finger is on the turntable: the idle turn waits until `release`. */
  grab(): void { this.held = true; }

  /** The held finger moved `dx` CSS px in `seconds`: the model turns with it, and keeps that speed as a flick. */
  spinBy(dx: number, seconds: number): void {
    this.spin += dx * SkinPreview.DragGain;
    this.velocity = (dx * SkinPreview.DragGain) / Math.max(SkinPreview.MinDragSeconds, seconds);
  }

  /** The finger lifted: the flick decays back to the idle turn. */
  release(): void { this.held = false; }

  /** Shows `target` wearing `skin` (null: stock). Resolves once it is on screen (after any download). */
  async show(target: PreviewTarget, skin: string | null): Promise<void> {
    const t = ++this.token;
    const L = lib();
    if (!L) return;
    const tier = target.kind === "troop" || target.kind === "pet" ? 1 : target.tier;
    const model = target.kind === "fortress" ? WornSkins.modelOf(skin) : null;
    const troop = WornSkins.troopModel(skin);
    const pet = target.kind === "pet" ? WornSkins.petModel(target.petId, skin) : "";
    // a tower skin with its own model (big kits) shows that model where it ships
    const towerSkin = target.kind === "tower" ? WornSkins.towerModelOf(skin) : null;
    const name = target.kind === "pet" ? pet : target.kind === "troop" ? troop : target.kind === "fortress" ? (model && L.shippedNames.has(`base_t${tier}__${model}`) ? `base_t${tier}__${model}` : `base_t${tier}`)
      : towerModelName(target.archId, tier, null, towerSkin, (n) => L.shippedNames.has(n));
    // the tower skin's kit GLB too, so the turntable shows its attachments from the first frame
    const kitAsset = target.kind === "tower" ? SkinKits.towerKit(skin)?.asset ?? null : null;
    const kitNeed = kitAsset && !L.has(`hq:${kitAsset}`) ? [kitAsset] : [];
    // a fortress skin's flyers fly in the preview too (the Dragonlord's dragon round the keep)
    const flyer = target.kind === "fortress" ? SkinFx.fortressKit(name)?.companion?.model ?? null : null;
    if (flyer && !L.hq(flyer)) kitNeed.push(flyer);
    // The model itself is a private copy (M31): the library's own lets its textures' CPU copies go once the main
    // canvas has uploaded them (cpuRelease.ts), and this canvas is a WebGL context of its own that uploads again.
    const [copies] = await Promise.all([L.copies([name]), kitNeed.length > 0 ? L.ensure(kitNeed) : Promise.resolve([])]);
    if (!copies.has(name) && !L.has(`hq:${name}`)) await L.ensure([name]);      // the copy failed: the library's, as before
    if (t !== this.token || this.disposed) { L.disposeCopies(copies); return; }
    this.clear();
    this.copies = copies;
    const root = L.withCopies(copies, (): THREE.Object3D => {
      if (target.kind === "pet") {
        const r = L.clone(`hq:${pet}`);
        WornSkins.dressPet(r, target.petId, skin);
        this.rig = new AnimRig(r, L.clipsOf(`hq:${pet}`));
        return r;
      }
      if (target.kind === "troop") {
        const r = L.clone(`hq:${troop}`);
        WornSkins.dressTroop(r, skin);
        this.rig = new AnimRig(r, L.clipsOf(`hq:${troop}`));
        return r;
      }
      const isBase = target.kind === "fortress";
      const m = buildTowerModel(isBase ? "base" : target.archId, isBase, tier, isBase ? model : towerSkin);
      WornSkins.dressTower(m.storeys, isBase ? "base" : target.archId, isBase, skin);
      this.rig = m.rig;
      return m.storeys;
    });
    if (target.kind !== this.shownKind) { this.spin = SkinPreview.StartSpin[target.kind]; this.shownKind = target.kind; }
    this.showcase = []; this.showcaseNext = 0; this.showcaseIn = Infinity;
    if (target.kind === "troop") { this.rig?.playLoop("idle", 1, true); this.showcaseCycle(["attack", "attack2"], SkinPreview.TroopShowcaseSeconds, SkinPreview.TroopShowcaseSeconds); }
    else if (target.kind === "pet") {
      this.rig?.playLoop("idle", 1, true);
      if (this.rig?.has("flourish")) this.showcaseCycle(["flourish"], SkinPreview.PetShowcaseFirst, SkinPreview.PetShowcaseSeconds);
    }
    // a fortress skin with its own reveal (the Dragonlord uncoiling, the Moonstone Keep's moonrise) plays it on pick
    if (target.kind === "fortress" && this.rig?.has("show")) this.rig.playOnce("show");
    const pivot = new THREE.Group();
    pivot.add(root);
    if (target.kind === "fortress" && model) {
      root.updateMatrixWorld(true);
      this.companion = FortressCompanion.of(root, name, new THREE.Box3().setFromObject(root), root);
    }
    root.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(root), centre = box.getCenter(new THREE.Vector3());
    root.position.sub(new THREE.Vector3(centre.x, centre.y, centre.z));
    this.model = pivot;
    this.scene.add(pivot);
    if (this.ground && this.key) {
      pivot.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.castShadow = true; });
      const sz = box.getSize(new THREE.Vector3()), ext = Math.max(sz.x, sz.y, sz.z);
      this.ground.position.set(0, -sz.y / 2 + 0.002, 0);
      this.ground.scale.setScalar(ext * 4);
      const sc = this.key.shadow.camera as THREE.OrthographicCamera;
      sc.left = sc.bottom = -ext; sc.right = sc.top = ext; sc.near = 0.01; sc.far = ext * 8;
      this.key.position.set(-0.6 * ext, 1.6 * ext, 0.9 * ext);
      this.key.target.position.set(0, -sz.y / 2, 0);
      sc.updateProjectionMatrix();
    }
    // the frame holds the model's larger extent (a sphere round the wide fortress left it small in the frame)
    const size = box.getSize(new THREE.Vector3());
    const r = Math.max(size.x, size.y, size.z) * SkinPreview.ExtentToRadius;
    const dist = r / Math.sin(THREE.MathUtils.degToRad(SkinPreview.FovDeg * SkinPreview.Fill) / 2);
    const tilt = THREE.MathUtils.degToRad(SkinPreview.TiltDeg);
    this.camera.position.set(0, Math.sin(tilt) * dist, Math.cos(tilt) * dist);
    this.camera.lookAt(0, 0, 0);
    this.camera.near = dist / 20; this.camera.far = dist * 4;
    this.camera.updateProjectionMatrix();
    this.start();
  }

  /** Cycles `clips` as one-shots over the idle: the first after `first` s, then every `every` s. */
  private showcaseCycle(clips: readonly string[], first: number, every: number): void {
    this.showcase = clips; this.showcaseNext = 0; this.showcaseIn = first; this.showcaseEvery = every;
  }

  private clear(): void {
    if (this.model) this.scene.remove(this.model);
    this.companion?.dispose();
    this.companion = null;
    this.model = null;
    this.rig = null;
    if (this.copies) lib()?.disposeCopies(this.copies);
    this.copies = null;
  }

  private start(): void {
    if (this.raf || this.disposed) return;
    this.last = performance.now();
    const tick = (now: number) => {
      // the sheet went without closing us (the hub torn down under it): free the context
      if (!this.canvas.isConnected && this.attached) { this.dispose(); return; }
      this.attached ||= this.canvas.isConnected;
      this.raf = requestAnimationFrame(tick);
      const dt = Math.min(0.1, (now - this.last) / 1000);
      this.last = now;
      if (!this.held) {
        this.velocity += (SkinPreview.IdleSpin - this.velocity) * Math.min(1, dt * SkinPreview.SpinDecay);
        this.spin += this.velocity * dt;
      }
      if (this.model) this.model.rotation.y = this.spin;
      // a troop shows off its weapon now and then: its two blows in turn (the halberd, the lantern's swing)
      if ((this.showcaseIn -= dt) <= 0 && this.rig && this.showcase.length > 0) {
        this.rig.playOnce(this.showcase[this.showcaseNext], false, null, { solo: true });
        this.showcaseNext = (this.showcaseNext + 1) % this.showcase.length;
        this.showcaseIn = this.showcaseEvery;
      }
      this.rig?.update(dt);
      this.companion?.update(dt, null);
      // the kits' GPU motion and the glow pulse run on the shared clock: keep it going under a sheet that paused the hub
      if (HqMaterials.time.value === this.clock) { HqMaterials.time.value += dt; HqMaterials.updateGlow(dt); }
      this.clock = HqMaterials.time.value;
      this.renderer.render(this.scene, this.camera);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Frees the canvas's GL context and the model copies (the kits' materials and geometries belong to the library). */
  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.clear();
    this.scene.environment?.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.closeContext();
    this.canvas.remove();
  }
  private readonly closeContext: () => void;
}
